import { ReactNode } from 'react';
import type { DisplayPublic } from '../api';

/**
 * Shared chrome wrapped around every channel renderer.
 *
 * Top bar: brand + display identity, connection dot, channel label, unpair.
 * The `transparent` variant strips the chrome entirely — used by
 * StreamOverlay so an OBS chroma-key capture isn't polluted.
 */
export function ChannelChrome({
  display,
  connected,
  onUnpair,
  variant = 'default',
  children,
}: {
  display: DisplayPublic;
  connected: boolean;
  onUnpair: () => void;
  variant?: 'default' | 'transparent' | 'minimal';
  children: ReactNode;
}) {
  if (variant === 'transparent') {
    return <div className="min-h-screen bg-transparent">{children}</div>;
  }

  const channel = display.channelType.replace(/_/g, ' ').toLowerCase();

  return (
    <div className="flex min-h-screen flex-col">
      {variant !== 'minimal' && (
        <header className="flex items-center justify-between border-b border-white/5 px-8 py-4 text-sm">
          <div className="flex items-center gap-4">
            <div className="font-extrabold tracking-tight">
              Score<span className="text-brand">Lync</span>
            </div>
            <div className="text-white/40">·</div>
            <div className="text-white/60">{display.name}</div>
            <div className="text-white/40">·</div>
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-white/40">
              {channel}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <ConnectionDot connected={connected} />
            <button
              onClick={() => {
                if (confirm('Unpair this display? It will need a new code.')) {
                  onUnpair();
                }
              }}
              className="text-white/30 transition hover:text-white/60"
            >
              Unpair
            </button>
          </div>
        </header>
      )}
      <main className="flex flex-1 flex-col">{children}</main>
    </div>
  );
}

function ConnectionDot({ connected }: { connected: boolean }) {
  return (
    <div className="flex items-center gap-2 text-white/50">
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          connected ? 'bg-emerald-400' : 'bg-amber-400'
        }`}
      />
      {connected ? 'Connected' : 'Reconnecting…'}
    </div>
  );
}
